import { Reveal } from "@components/ui";

interface StepCardProps {
  step: number;
  title: string;
  description: string;
  linkText: string;
  linkHref: string;
  icon: React.ReactNode;
}

function CreditCardIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="2"
        y="5"
        width="20"
        height="14"
        rx="2"
        stroke="#f24d2b"
        strokeWidth="1.8"
      />
      <path d="M2 10h20" stroke="#f24d2b" strokeWidth="1.8" />
      <rect x="5" y="14" width="5" height="2" rx="0.5" fill="#f24d2b" />
    </svg>
  );
}

function LightningBoltIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 2L4.094 12.688a.5.5 0 00.406.812H11l-1 8.5 9-10.688a.5.5 0 00-.406-.812H13l1-8.5z"
        stroke="#f24d2b"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GaugeMeterIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 21a9 9 0 110-18 9 9 0 010 18z"
        stroke="#f24d2b"
        strokeWidth="1.8"
      />
      <path
        d="M12 7v5l3.5 2"
        stroke="#f24d2b"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="1.5" fill="#f24d2b" />
    </svg>
  );
}

function StepCard({
  step,
  title,
  description,
  linkText,
  linkHref,
  icon,
}: StepCardProps) {
  const watermark = String(step).padStart(2, "0");

  return (
    <div className="group relative w-full h-full rounded-[12px] md:rounded-[24px] bg-white flex flex-col items-start p-4 md:py-10 md:px-6 gap-5 md:gap-7 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 overflow-hidden">
      {/* Watermark number */}
      <span className="absolute top-3 right-4 md:top-4 md:right-6 text-[64px] md:text-[80px] lg:text-[96px] font-bold leading-none text-[#f0f0f3] select-none pointer-events-none font-albert-sans transition-colors duration-300 group-hover:text-[#e8e9ed]">
        {watermark}
      </span>

      <div className="relative z-10 self-stretch flex-1 flex flex-col items-start gap-6 md:gap-7">
        {/* Icon container */}
        <div className="w-10 h-10 md:w-11 md:h-11 rounded-[10px] bg-[#fef0ec] flex items-center justify-center">
          {icon}
        </div>

        {/* Step Number Badge + Title */}
        <div className="self-stretch flex flex-col items-start justify-center gap-[15px]">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#f24d2b] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#d9401f]">
            <span className="text-sm md:text-[20px] leading-[120%] font-bold text-white font-albert-sans">
              {step}
            </span>
          </div>
          <h2 className="self-stretch text-sm md:text-lg lg:text-[32px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans leading-[1.2]">
            {title}
          </h2>
        </div>

        {/* Description */}
        <p className="self-stretch text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.5]">
          {description}
        </p>
      </div>

      {/* Link */}
      <a
        href={linkHref}
        className="relative z-10 flex items-center justify-center gap-2.5 pb-2.5 border-b border-[#0a0e2e] hover:opacity-80 transition-opacity"
      >
        <span className="text-xs md:text-base lg:text-[20px] tracking-[-0.04em] font-normal text-[#0a0e2e] font-albert-sans">
          {linkText}
        </span>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover:translate-x-1"
        >
          <path
            d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2h12.172z"
            fill="#0a0e2e"
          />
        </svg>
      </a>
    </div>
  );
}

const steps: StepCardProps[] = [
  {
    step: 1,
    title: "Pay Your Gas Bill Securely",
    description:
      "GASIS offers a secure, fast, and transparent online payment system. You can pay your gas bill anytime using credit or debit card, Apple Pay, or bank transfer.",
    linkText: "Go to payment page",
    linkHref: "/pay-bill",
    icon: <CreditCardIcon />,
  },
  {
    step: 2,
    title: "Instant Bill Payment Without Logging In",
    description:
      "Enter your Account or Invoice Number and make a one-time payment securely. Perfect for tenants or quick settlements.",
    linkText: "Pay Instantly",
    linkHref: "/pay-bill",
    icon: <LightningBoltIcon />,
  },
  {
    step: 3,
    title: "Submit Your Meter Reading in Seconds",
    description:
      "Submitting your meter reading regularly ensures your bills reflect your exact consumption. You can enter the reading manually or upload a clear photo of your meter display.",
    linkText: "Submit Reading",
    linkHref: "/meter-reading",
    icon: <GaugeMeterIcon />,
  },
];

export function PaymentStepsSection() {
  return (
    <section className="w-full bg-[#f6f6f9] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {steps.map((step, index) => (
            <Reveal
              key={step.step}
              direction="up"
              delay={index * 120}
              className="flex"
            >
              <StepCard {...step} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
